import React from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
} from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';

const COMMENTS_QUERY = gql`
  query AdminComments($id: ID!, $isSprint: Boolean!, $isItem: Boolean!, $limit: Int) {
    sprint: sprint(id: $id) @include(if: $isSprint) {
      id
      comments(limit: $limit) { id comment created_at user { id allowed_name profile_picture } author_role }
      comment_count
      unread_comment_count
    }
    item: action_plan_item(id: $id) @include(if: $isItem) {
      id
      comments(limit: $limit) { id comment created_at user { id allowed_name profile_picture } author_role }
      comment_count
      unread_comment_count
    }
  }
`;

const ADD_TO_SPRINT = gql`
  mutation AddCommentToSprint($sprint_id: ID!, $comment: String!) {
    add_comment_to_sprint(sprint_id: $sprint_id, comment: $comment) { id }
  }
`;

const ADD_TO_ITEM = gql`
  mutation AddCommentToActionPlanItem($item_id: ID!, $comment: String!) {
    add_comment_to_action_plan_item(item_id: $item_id, comment: $comment) { id }
  }
`;

const MARK_READ = gql`
  mutation MarkCommentsRead($commentable_type: String!, $commentable_id: ID!) {
    mark_comments_read(commentable_type: $commentable_type, commentable_id: $commentable_id)
  }
`;

const formatDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return '';
  }
};

const roleColor = (role) => {
  const r = String(role || '').toLowerCase();
  if (r === 'owner') return 'primary';
  if (r === 'admin') return 'secondary';
  return 'default';
};

const resolveAvatarSrc = (val) => {
  if (!val) return undefined;
  if (val.startsWith('http') || val.startsWith('/')) return val;
  return `/assets/${val}`;
};

const AdminCommentsSection = ({ commentableId, commentableType, title = "Coach's Corner", maxHeight = 360 }) => {
  const isSprint = commentableType === 'Sprint';
  const isItem = commentableType === 'ActionPlanItem';

  const { data, loading, error, refetch } = useQuery(COMMENTS_QUERY, {
    variables: { id: commentableId, isSprint, isItem, limit: 100 },
    fetchPolicy: 'cache-and-network',
  });
  const [addToSprint] = useMutation(ADD_TO_SPRINT);
  const [addToItem] = useMutation(ADD_TO_ITEM);
  const [markRead] = useMutation(MARK_READ);
  const [text, setText] = React.useState('');

  const node = isSprint ? data?.sprint : data?.item;
  const comments = node?.comments || [];
  const commentCount = node?.comment_count || 0;
  const unread = node?.unread_comment_count || 0;

  React.useEffect(() => {
    if (commentableId && commentableType) {
      markRead({ variables: { commentable_type: commentableType, commentable_id: commentableId } }).catch(() => {});
    }
  }, [commentableId, commentableType, markRead]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const body = text.trim();
    if (!body) return;
    try {
      if (isSprint) {
        await addToSprint({ variables: { sprint_id: commentableId, comment: body } });
      } else if (isItem) {
        await addToItem({ variables: { item_id: commentableId, comment: body } });
      }
      setText('');
      await refetch();
    } catch (_) {}
  };

  return (
    <Card elevation={0} variant="outlined">
      <CardHeader
        title={title}
        subheader={loading ? 'Loading comments…' : `${commentCount} comment${commentCount === 1 ? '' : 's'}${unread ? ` • ${unread} new` : ''}`}
      />
      <Divider />
      <CardContent style={{ paddingTop: 8 }}>
        {error && (
          <Typography color="error" variant="body2">Failed to load comments</Typography>
        )}
        {!error && (
          <Box>
            <Box style={{ maxHeight, overflowY: 'auto' }}>
              {loading && comments.length === 0 ? (
                <Box display="flex" alignItems="center" justifyContent="center" py={2}><CircularProgress size={20} /></Box>
              ) : (
                <List dense>
                  {comments.map((c) => (
                    <ListItem key={c.id} alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar src={resolveAvatarSrc(c?.user?.profile_picture)} alt={c?.user?.allowed_name || 'User'} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gridGap={8}>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>{c?.user?.allowed_name}</Typography>
                            {c?.author_role && <Chip size="small" label={String(c.author_role).replace(/\b\w/g, (m)=>m.toUpperCase())} color={roleColor(c.author_role)} />}
                            <Typography variant="caption" color="textSecondary">{formatDateTime(c.created_at)}</Typography>
                          </Box>
                        }
                        secondary={
                          <Box mt={0.5}>
                            <Typography variant="body2">{c.comment}</Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                  {comments.length === 0 && !loading && (
                    <Box py={1} px={1}>
                      <Typography variant="body2" color="textSecondary">No comments yet.</Typography>
                    </Box>
                  )}
                </List>
              )}
            </Box>

            <Box component="form" onSubmit={handleSubmit} display="flex" alignItems="center" mt={1}>
              <TextField
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Add a comment…"
                variant="outlined"
                size="small"
                fullWidth
                onKeyDown={(e)=>{ if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSubmit(e); }}
              />
              <IconButton color="primary" onClick={handleSubmit} disabled={!text.trim()} aria-label="send" style={{ marginLeft: 8 }}>
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminCommentsSection;


